import { Fragment, type ReactNode } from "react";

type ReactMarkdownProps = {
  children: string;
  className?: string;
};

type ListItem = {
  type: "unordered" | "ordered" | "checkbox";
  indent: number;
  text: string;
  checked?: boolean;
};

const ORDERED_LIST_PATTERN = /^(\d+[.)])\s+(.*)$/;
const UNORDERED_LIST_PATTERN = /^[-*]\s+(.*)$/;
const CHECKBOX_PATTERN = /^\[(x|X| )\]\s+(.*)$/;
const BLOCKQUOTE_PATTERN = /^>\s?(.*)$/;

const INLINE_PATTERN = /(\*\*[\s\S]+?\*\*|__[\s\S]+?__|~~[\s\S]+?~~|\*[^*]+\*|_[^_]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

const normalizeMarkdown = (content: string): string => {
  return content
    .replace(/(_|\*)[ \t]+([^\n]+?)[ \t]+\1/g, "$1$2$1")
    .replace(/(\*\*|__)[ \t]+([^\n]+?)[ \t]+\1/g, "$1$2$1")
    .replace(/([A-Za-z0-9])_([^_\n]+)_([A-Za-z0-9])/g, "$1 _$2_ $3")
    .replace(/([A-Za-z0-9])\*([^*\n]+)\*([A-Za-z0-9])/g, "$1 *$2* $3")
    .replace(/([A-Za-z0-9])(__[^_\n]+__)([A-Za-z0-9])/g, "$1 $2 $3")
    .replace(/([A-Za-z0-9])(\*\*[^*\n]+\*\*)([A-Za-z0-9])/g, "$1 $2 $3");
};

const renderInline = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  text.replace(INLINE_PATTERN, (match, _, offset: number) => {
    if (offset > lastIndex) {
      nodes.push(text.slice(lastIndex, offset));
    }

    if ((match.startsWith("**") && match.endsWith("**")) || (match.startsWith("__") && match.endsWith("__"))) {
      nodes.push(<strong key={`${offset}-strong`}>{renderInline(match.slice(2, -2))}</strong>);
    } else if ((match.startsWith("*") && match.endsWith("*")) || (match.startsWith("_") && match.endsWith("_"))) {
      nodes.push(<em key={`${offset}-em`}>{renderInline(match.slice(1, -1))}</em>);
    } else if (match.startsWith("~~") && match.endsWith("~~")) {
      nodes.push(<del key={`${offset}-del`}>{renderInline(match.slice(2, -2).trim())}</del>);
    } else if (match.startsWith("`") && match.endsWith("`")) {
      nodes.push(
        <code key={`${offset}-code`} className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs text-gray-800">
          {match.slice(1, -1)}
        </code>
      );
    } else if (match.startsWith("[") && match.includes("](")) {
      const closeBracketIndex = match.indexOf("](");
      const linkText = match.slice(1, closeBracketIndex);
      const linkHref = match.slice(closeBracketIndex + 2, -1);
      nodes.push(
        <a key={`${offset}-link`} href={linkHref} target="_blank" rel="noreferrer" className="underline token-link-primary">
          {linkText}
        </a>
      );
    } else {
      nodes.push(match);
    }

    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

const parseListItem = (line: string): ListItem | null => {
  const match = line.match(/^(\s*)(.*)$/);
  if (!match) return null;

  const indentSpaces = match[1].replace(/\t/g, "    ").length;
  const indent = Math.floor(indentSpaces / 2);
  const trimmed = match[2];

  const checkboxMatch = trimmed.match(CHECKBOX_PATTERN);
  if (checkboxMatch) {
    return {
      type: "checkbox",
      indent,
      checked: checkboxMatch[1].toLowerCase() === "x",
      text: checkboxMatch[2]
    };
  }

  const unorderedMatch = trimmed.match(UNORDERED_LIST_PATTERN);
  if (unorderedMatch) {
    return { type: "unordered", indent, text: unorderedMatch[1] };
  }

  const orderedMatch = trimmed.match(ORDERED_LIST_PATTERN);
  if (orderedMatch) {
    return { type: "ordered", indent, text: orderedMatch[2] };
  }

  return null;
};

export default function ReactMarkdown({ children, className }: ReactMarkdownProps) {
  if (!children?.trim()) {
    return <p className={className}>No description provided.</p>;
  }

  const normalizedContent = normalizeMarkdown(children);
  const lines = normalizedContent.split(/\r?\n/);
  const blocks: ReactNode[] = [];
  let listItems: ListItem[] = [];
  let codeFence: string[] = [];
  let inCodeFence = false;

  const flushLists = () => {
    if (!listItems.length) return;

    blocks.push(
      <ul key={`list-${blocks.length}`} className="space-y-1">
        {listItems.map((item, index) => (
          <li key={`li-${index}`} style={{ marginLeft: `${item.indent * 12}px` }} className="text-sm text-gray-700">
            {item.type === "checkbox" ? (
              <span className="inline-flex items-center gap-2">
                <input type="checkbox" checked={Boolean(item.checked)} readOnly className="h-3.5 w-3.5" />
                <span>{renderInline(item.text)}</span>
              </span>
            ) : item.type === "ordered" ? (
              <span><span className="mr-1">1.</span>{renderInline(item.text)}</span>
            ) : (
              <span><span className="mr-1">•</span>{renderInline(item.text)}</span>
            )}
          </li>
        ))}
      </ul>
    );
    listItems = [];
  };

  const flushCodeFence = () => {
    if (!codeFence.length) return;
    blocks.push(
      <pre key={`pre-${blocks.length}`} className="overflow-x-auto rounded bg-gray-900 p-3 text-xs text-gray-100">
        <code>{codeFence.join("\n")}</code>
      </pre>
    );
    codeFence = [];
  };

  lines.forEach((rawLine, index) => {
    const line = rawLine.trimEnd();

    if (line.trim().startsWith("```")) {
      if (inCodeFence) {
        flushCodeFence();
        inCodeFence = false;
      } else {
        flushLists();
        inCodeFence = true;
      }
      return;
    }

    if (inCodeFence) {
      codeFence.push(line);
      return;
    }

    if (!line.trim()) {
      flushLists();
      return;
    }

    const parsedListItem = parseListItem(line);
    if (parsedListItem) {
      listItems.push(parsedListItem);
      return;
    }

    flushLists();

    const trimmed = line.trim();

    const blockquoteMatch = trimmed.match(BLOCKQUOTE_PATTERN);
    if (blockquoteMatch) {
      blocks.push(
        <blockquote key={`bq-${index}`} className="border-l-4 border-gray-300 pl-3 italic text-gray-600">
          {renderInline(blockquoteMatch[1])}
        </blockquote>
      );
      return;
    }

    if (trimmed.startsWith("### ")) {
      blocks.push(
        <h5 key={`h5-${index}`} className="text-sm font-semibold">
          {renderInline(trimmed.slice(4))}
        </h5>
      );
      return;
    }

    if (trimmed.startsWith("## ")) {
      blocks.push(
        <h4 key={`h4-${index}`} className="text-base font-semibold">
          {renderInline(trimmed.slice(3))}
        </h4>
      );
      return;
    }

    if (trimmed.startsWith("# ")) {
      blocks.push(
        <h3 key={`h3-${index}`} className="text-lg font-semibold">
          {renderInline(trimmed.slice(2))}
        </h3>
      );
      return;
    }

    blocks.push(<p key={`p-${index}`}>{renderInline(trimmed)}</p>);
  });

  flushLists();
  flushCodeFence();

  return (
    <div className={className}>
      {blocks.map((block, index) => (
        <Fragment key={`block-${index}`}>{block}</Fragment>
      ))}
    </div>
  );
}
